import { MAX_ALLOWED_QQ_TEXT_LENGTH } from '@mm/constants/widgets';
import { ENTER_KEY_NAME, TAB_KEY_NAME } from '@mm/constants/keys-codes';
import { StoreState } from '@mm/state/store-interfaces';
import classnames from 'classnames';
import React, {
  ChangeEvent,
  MutableRefObject,
  useEffect,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import AttributeContainingTextInputComponent from '../../../../editor/components/AttributeContainingTextInput/AttributeContainingTextInput';
import { useIsLeadGeneratorBuilderRoute } from '../../../../modules/FreeLeadGenerator/use-is-lead-generator-builder-route';
import { TextAreaSidePanel } from '../../../components/TextAreaSidePanel/TextAreaSidePanel';
import { useInputRef } from '../use-input-ref';
import './BubbleTextInput.pcss';

export type InputFieldRefType = MutableRefObject<
  HTMLInputElement | HTMLTextAreaElement | undefined
>;

type Props = {
  value: string | undefined;
  editMode: boolean;
  onClick?: () => void;
  onChange?: (v: string) => void;
  updateText?: (newText: string) => any;
  updateConfigField?: (text: string) => void;
  className?: string;
  maxLength?: number;
  multiline?: boolean;
  multilineMaxRows?: number;
  multilineMinRows?: number;
  showTextLength?: boolean;
  showEmojiPicker?: boolean;
  emojiPickerColor?: string;
  attributePickerColor?: string;
  showAttributePicker?: boolean;
  orientation?: 'horizontal' | 'vertical';
  disabled?: boolean;
  showOnlyNameAttributes?: boolean;
  autofocus?: boolean;
  placeholder?: string;
  saveOnKeyboard?: boolean;
  onBlur?: (value?: string) => any;
  inputFieldRefToSet?: InputFieldRefType;
  maxSmsChannelTextLength?: number;
  maxOthersChannelTextLength?: number;
  extraTextLength?: number;
  hasQQButtons?: boolean;
  setEditMode?: (editMode: boolean) => void;
  showCountByChannel?: boolean;
  newCharacterCountDesign?: boolean;
  smsStructure?: string;
  onSaveClick?: () => void;
  isAutomationsSlideout?: boolean;
};

export const BubbleTextInput = React.forwardRef(
  (props: Props, ref: MutableRefObject<HTMLDivElement | null>) => {
    const {
      editMode,
      onClick = () => {},
      updateText = () => {},
      value,
      className,
      maxLength,
      multiline = true,
      multilineMaxRows,
      multilineMinRows,
      showTextLength = true,
      showEmojiPicker = true,
      emojiPickerColor,
      showAttributePicker = true,
      attributePickerColor,
      orientation = 'vertical',
      disabled,
      updateConfigField,
      showOnlyNameAttributes = false,
      autofocus = false,
      placeholder,
      saveOnKeyboard = false,
      onBlur = () => {},
      onChange = () => {},
      inputFieldRefToSet = {} as InputFieldRefType,
      newCharacterCountDesign = false,
      maxSmsChannelTextLength,
      maxOthersChannelTextLength,
      extraTextLength = 0,
      hasQQButtons,
      setEditMode,
      showCountByChannel = false,
      smsStructure,
      onSaveClick,
      isAutomationsSlideout,
    } = props;
    const { inputRef, memoizedSetInputRef } = useInputRef(autofocus);
    inputFieldRefToSet.current = inputRef;
    const attributes = useSelector(
      (state: StoreState) => state.attribute.entities,
    );
    const [localBodyText, setLocalBodyText] = useState(value || '');
    const bodyText = editMode ? localBodyText : value;
    const insertEmojiOrAttributeIntoText = (newText: string) => {
      setLocalBodyText(newText);
      updateText(newText);
    };

    const saveTextChanges = () => {
      if (value !== localBodyText) {
        updateText(localBodyText);
      }
      onBlur(localBodyText);
    };

    useEffect(() => {
      if (localBodyText !== value) setLocalBodyText(value || '');
    }, [value]);

    const onTextAreaChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
      if (editMode) {
        setLocalBodyText(e.target.value);
        if (updateConfigField) {
          updateConfigField(e.target.value);
        }
      }
    };

    const onValueInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!saveOnKeyboard) return;
      if (e.key === TAB_KEY_NAME || e.key === ENTER_KEY_NAME) {
        if (value !== localBodyText) {
          updateText(localBodyText);
        }
      }
    };

    useEffect(() => {
      onChange(localBodyText);
    }, [localBodyText]);

    const isLeadGeneratorRoute = useIsLeadGeneratorBuilderRoute();

    const textAreaSidePanel = (
      <TextAreaSidePanel
        passedRef={inputRef}
        text={localBodyText || ''}
        updateText={insertEmojiOrAttributeIntoText}
        maxTextLength={maxLength || MAX_ALLOWED_QQ_TEXT_LENGTH}
        currentTextLength={localBodyText.length}
        showTextLength={showTextLength}
        showEmojiPicker={showEmojiPicker}
        emojiPickerColor={emojiPickerColor}
        attributePickerColor={attributePickerColor}
        showAttributePicker={isLeadGeneratorRoute ? false : showAttributePicker}
        orientation={orientation}
        showOnlyNameAttributes={showOnlyNameAttributes}
        newCharacterCountDesign={newCharacterCountDesign}
        countByChannel={showCountByChannel}
        maxSmsChannelTextLength={maxSmsChannelTextLength}
        maxOthersChannelTextLength={maxOthersChannelTextLength}
        onSaveClick={() => {
          onSaveClick?.();
          setEditMode?.(false);
        }}
        extraTextLength={extraTextLength}
        hasQQButtons={hasQQButtons}
        smsStructure={smsStructure}
        isAutomationsSlideout={isAutomationsSlideout}
      />
    );

    return (
      <div
        className={classnames('bubble-text-input', className, {
          'bubble-text-input--edit-mode': editMode,
          'bubble-text-input--new-design': newCharacterCountDesign,
          'bubble-text-input--active-mode': !disabled,
          'bubble-text-input--automations_slideout': isAutomationsSlideout,
        })}
        ref={ref}
      >
        <div
          onClick={onClick}
          className="bubble-text-input__text-area-input-wrapper"
          onKeyDown={onValueInputKeyDown}
        >
          <AttributeContainingTextInputComponent
            className="bubble-text-input__text-area"
            disabled={disabled}
            autosize={true}
            draggable={false}
            onChange={onTextAreaChange}
            maxLength={maxLength || MAX_ALLOWED_QQ_TEXT_LENGTH}
            onBlur={saveTextChanges}
            multiline={multiline}
            multilineMaxRows={multilineMaxRows}
            multilineMinRows={multilineMinRows}
            value={bodyText}
            inputRef={memoizedSetInputRef}
            attributes={attributes}
            showOnlyNameAttributes={showOnlyNameAttributes}
            enabledSuggestions={showAttributePicker}
            placeholder={placeholder}
            isAutomationsSlideout={isAutomationsSlideout}
          />
          {editMode &&
            (!newCharacterCountDesign || isAutomationsSlideout) &&
            textAreaSidePanel}
        </div>
        {editMode && newCharacterCountDesign && textAreaSidePanel}
      </div>
    );
  },
);
